/**
 * AppManagementPortalPage · v2.7.65+E128 (2026-02-XX)
 * ═════════════════════════════════════════════════════════════════════
 * App Management Portal — super_user / general_partner / dispatch surface.
 *
 * RBAC gate: server-side `/admin/app-management/access-check` returns
 * 200 + `{authorized, role, can_write}` for approved roles, 403 otherwise.
 * The page mirrors MasterAccessPage's shell (loader → denied → grid) so
 * the deny-flow is identical for operators.
 *
 * Theme: the Taxi color palette from the user's JSON is applied via CSS
 * variables scoped to a `data-app-management-portal="1"` root wrapper
 * so the yellow / ruby / danger accents live ONLY on this page and don't
 * bleed into the rest of the admin console.
 *
 *   --bt-taxi-primary        · #FFD200  (Taxi Yellow)
 *   --bt-taxi-secondary      · #E0115F  (Ruby)
 *   --bt-taxi-danger         · #D32F2F
 *   --bt-taxi-neutral-dark   · #212529
 *   --bt-taxi-neutral-light  · #F8F9FA
 *   --bt-taxi-surface        · #343A40
 *
 * Historical ride heatmap logic stays PAUSED per user directive — the
 * portal surfaces this via a banner + a locked-flag row in the Feature
 * Flags card. The backend blocks any enable-attempt with a 400.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, Loader2, Lock, ShieldCheck, Activity, ToggleLeft,
  UsersRound, Server, ClipboardList, Route as RouteIcon, RefreshCw,
  Pause, AlertTriangle, MapPinned,
} from "lucide-react";

/* ──────────────────────────── Scoped theme ───────────────────────────
 * These CSS variables live only under the portal root so nothing else
 * in the admin console inherits the yellow/ruby accents.
 */
const PORTAL_THEME_STYLE = {
  "--bt-taxi-primary":       "#FFD200",
  "--bt-taxi-secondary":     "#E0115F",
  "--bt-taxi-danger":        "#D32F2F",
  "--bt-taxi-neutral-dark":  "#212529",
  "--bt-taxi-neutral-light": "#F8F9FA",
  "--bt-taxi-surface":       "#343A40",
  backgroundColor:           "var(--bt-taxi-neutral-dark)",
  color:                     "var(--bt-taxi-neutral-light)",
  minHeight:                 "100vh",
};

const CARD_STYLE = {
  backgroundColor: "var(--bt-taxi-surface)",
  borderColor:     "rgba(255, 210, 0, 0.20)",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

/* ──────────────────────────── Sub-components ─────────────────────── */
function PortalCard({ Icon, title, accent = "primary", children, testid, headerRight }) {
  const dot = accent === "secondary"
    ? "var(--bt-taxi-secondary)"
    : accent === "danger"
    ? "var(--bt-taxi-danger)"
    : "var(--bt-taxi-primary)";
  return (
    <div
      data-testid={testid}
      className="rounded-xl border p-5 shadow-lg"
      style={CARD_STYLE}
    >
      <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div
            className="rounded-lg p-2"
            style={{ backgroundColor: "rgba(255,255,255,0.05)", border: `1px solid ${dot}` }}
          >
            <Icon className="h-5 w-5" style={{ color: dot }} />
          </div>
          <h2 className="text-lg font-semibold tracking-tight" style={{ color: "var(--bt-taxi-neutral-light)" }}>
            {title}
          </h2>
        </div>
        {headerRight}
      </div>
      {children}
    </div>
  );
}

/* ─────────────────────────────── Cards ──────────────────────────── */
function ModuleRegistryCard() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/app-management/modules");
      setRows(data?.modules || []);
    } catch (_e) { setRows([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <PortalCard
      Icon={Activity}
      title="Module Registry"
      testid="portal-card-modules"
      headerRight={
        <Button
          size="sm" variant="ghost"
          data-testid="portal-modules-refresh"
          onClick={load} disabled={loading}
          className="text-zinc-300 hover:text-white"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      }
    >
      <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {rows.map((m) => {
          const isRoutable = typeof m.route === "string" && m.route.startsWith("/") && m.status === "active";
          return (
            <li key={m.key} data-testid={`portal-module-row-${m.key}`}>
              <button
                onClick={() => isRoutable && navigate(m.route)}
                disabled={!isRoutable}
                className={`w-full flex items-center justify-between gap-3 text-left text-sm rounded-md px-2 py-1.5 transition ${
                  isRoutable ? "hover:bg-white/5 cursor-pointer" : "cursor-default"
                }`}
              >
                <div className="min-w-0">
                  <div className="font-medium truncate" style={{ color: "var(--bt-taxi-neutral-light)" }}>{m.label}</div>
                  <div className="text-xs text-zinc-400 truncate">{m.route}</div>
                </div>
                <span
                  className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full whitespace-nowrap"
                  style={{
                    backgroundColor: m.status === "paused" ? "rgba(211,47,47,0.15)" : "rgba(255,210,0,0.15)",
                    color:           m.status === "paused" ? "var(--bt-taxi-danger)" : "var(--bt-taxi-primary)",
                    border:          `1px solid ${m.status === "paused" ? "var(--bt-taxi-danger)" : "var(--bt-taxi-primary)"}`,
                  }}
                >{m.status}</span>
              </button>
            </li>
          );
        })}
        {!rows.length && !loading && (
          <li className="text-sm text-zinc-400">No modules registered.</li>
        )}
      </ul>
    </PortalCard>
  );
}

function ZoneSummaryCard() {
  const navigate = useNavigate();
  const [zones, setZones] = useState({ draft: 0, published: 0, total: 0 });
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/driver-app-manager/zones");
      const rows = data?.zones || [];
      setZones({
        draft:     rows.filter((z) => z.status === "draft").length,
        published: rows.filter((z) => z.status === "published").length,
        total:     rows.length,
      });
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <PortalCard
      Icon={MapPinned}
      title="Zone Inventory"
      testid="portal-card-zones"
      accent="secondary"
      headerRight={
        <Button
          size="sm" variant="ghost"
          data-testid="portal-zones-refresh"
          onClick={load} disabled={loading}
          className="text-zinc-300 hover:text-white"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      }
    >
      {denied ? (
        <div className="text-sm text-zinc-400" data-testid="portal-zones-denied">
          Zone data is L5-only (super_user or general_partner).
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="rounded-lg p-3 text-center border"
                 data-testid="portal-zones-total"
                 style={{ borderColor: "var(--bt-taxi-primary)", backgroundColor: "rgba(255,210,0,0.05)" }}>
              <div className="text-2xl font-extrabold" style={{ color: "var(--bt-taxi-primary)" }}>{zones.total}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-1">Total</div>
            </div>
            <div className="rounded-lg p-3 text-center border"
                 data-testid="portal-zones-published"
                 style={{ borderColor: "#10B981", backgroundColor: "rgba(16,185,129,0.05)" }}>
              <div className="text-2xl font-extrabold text-emerald-400">{zones.published}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-1">Published</div>
            </div>
            <div className="rounded-lg p-3 text-center border"
                 data-testid="portal-zones-draft"
                 style={{ borderColor: "#F59E0B", backgroundColor: "rgba(245,158,11,0.05)" }}>
              <div className="text-2xl font-extrabold text-amber-400">{zones.draft}</div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-1">Draft</div>
            </div>
          </div>
          <Button
            onClick={() => navigate("/admin/driver-app-manager")}
            data-testid="portal-zones-open"
            className="w-full text-black font-bold"
            style={{ backgroundColor: "var(--bt-taxi-primary)" }}
          >
            <MapPinned className="h-4 w-4 mr-2" />Open Driver App Manager
          </Button>
        </>
      )}
    </PortalCard>
  );
}

function FeatureFlagsCard({ canWrite }) {
  const [flags, setFlags] = useState({});
  const [meta, setMeta] = useState({});
  const [pending, setPending] = useState("");
  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/app-management/feature-flags");
      setFlags(data?.flags || {});
      setMeta({ updated_at: data?.updated_at, updated_by: data?.updated_by });
    } catch (_e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, next) => {
    if (!canWrite) return;
    setPending(key);
    try {
      await adminApi.patch(`/admin/app-management/feature-flags/${key}`, { enabled: next });
      await load();
      toast.success(`${key} → ${next ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setPending(""); }
  };

  return (
    <PortalCard Icon={ToggleLeft} title="Feature Flags" testid="portal-card-flags" accent="secondary">
      <ul className="space-y-3">
        {Object.entries(flags).map(([k, v]) => {
          const isHeatmap = k === "ride_heatmap_enabled";
          return (
            <li
              key={k}
              data-testid={`portal-flag-row-${k}`}
              className="flex items-center justify-between gap-3 text-sm"
            >
              <div className="min-w-0">
                <div className="font-medium" style={{ color: "var(--bt-taxi-neutral-light)" }}>
                  {k}
                </div>
                {isHeatmap && (
                  <div className="text-[11px] text-rose-300 flex items-center gap-1 mt-0.5">
                    <Pause className="h-3 w-3" />Paused per operations directive
                  </div>
                )}
              </div>
              <Switch
                data-testid={`portal-flag-toggle-${k}`}
                checked={!!v}
                disabled={!canWrite || pending === k || isHeatmap}
                onCheckedChange={(n) => toggle(k, n)}
              />
            </li>
          );
        })}
      </ul>
      {meta.updated_at && (
        <div className="mt-4 text-xs text-zinc-400" data-testid="portal-flags-meta">
          Last updated {fmt(meta.updated_at)} by {meta.updated_by || "unknown"}
        </div>
      )}
      {!canWrite && (
        <div className="mt-3 text-xs" style={{ color: "var(--bt-taxi-secondary)" }}>
          Read-only for dispatch role.
        </div>
      )}
    </PortalCard>
  );
}

function RbacSnapshotCard() {
  const [snap, setSnap] = useState({ total_active_users: 0, by_role: [], gated_buckets: {} });
  useEffect(() => {
    adminApi.get("/admin/app-management/rbac-snapshot")
      .then((r) => setSnap(r.data || {})).catch(() => {});
  }, []);
  return (
    <PortalCard Icon={UsersRound} title="RBAC Audit Snapshot" testid="portal-card-rbac">
      <div className="grid grid-cols-3 gap-3 mb-4">
        {["super_user", "general_partner", "dispatch"].map((k) => (
          <div key={k} className="rounded-lg p-3 text-center border"
               data-testid={`portal-rbac-bucket-${k}`}
               style={{ borderColor: "var(--bt-taxi-primary)", backgroundColor: "rgba(255,210,0,0.05)" }}>
            <div className="text-2xl font-extrabold" style={{ color: "var(--bt-taxi-primary)" }}>
              {(snap.gated_buckets || {})[k] || 0}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-1">{k.replace("_", " ")}</div>
          </div>
        ))}
      </div>
      <div className="text-xs text-zinc-400 mb-2">
        {snap.total_active_users || 0} active users across {snap.by_role?.length || 0} distinct roles
      </div>
      <ul className="space-y-1 max-h-40 overflow-y-auto text-sm">
        {(snap.by_role || []).map((r) => (
          <li key={r.role}
              data-testid={`portal-rbac-role-${r.role}`}
              className="flex items-center justify-between">
            <span className="text-zinc-200">{r.role}</span>
            <span className="text-zinc-400 font-mono">{r.count}</span>
          </li>
        ))}
      </ul>
    </PortalCard>
  );
}

function DeploymentInfoCard() {
  const [info, setInfo] = useState({});
  useEffect(() => {
    adminApi.get("/admin/app-management/deployment")
      .then((r) => setInfo(r.data || {})).catch(() => {});
  }, []);
  return (
    <PortalCard Icon={Server} title="Deployment Info" testid="portal-card-deployment">
      <dl className="space-y-2 text-sm">
        {[
          ["Environment",     info.environment],
          ["Backend Version", info.backend_version],
          ["Python Env",      info.python_env],
          ["Mongo DB",        info.mongo_db],
          ["As Of",           fmt(info.as_of)],
        ].map(([label, val]) => (
          <div key={label} className="flex items-center justify-between gap-2">
            <dt className="text-zinc-400">{label}</dt>
            <dd
              className="font-mono text-amber-100 truncate"
              style={{ color: "#FEF3C7" }}
              data-testid={`portal-deploy-${label.toLowerCase().replace(/ /g, "-")}`}
            >{val ?? "—"}</dd>
          </div>
        ))}
      </dl>
    </PortalCard>
  );
}

function AuditTailCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/app-management/audit?limit=10");
      setItems(data?.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <PortalCard
      Icon={ClipboardList}
      title="Recent Config Changes"
      testid="portal-card-audit"
      headerRight={
        <Button
          size="sm" variant="ghost"
          data-testid="portal-audit-refresh"
          onClick={load} disabled={loading}
          className="text-zinc-300 hover:text-white"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      }
    >
      {items.length === 0 && !loading && (
        <div className="text-sm text-zinc-400" data-testid="portal-audit-empty">
          No admin-side config changes recorded yet.
        </div>
      )}
      <ul className="space-y-2 max-h-60 overflow-y-auto pr-1">
        {items.map((row, idx) => (
          <li key={idx}
              data-testid={`portal-audit-row-${idx}`}
              className="text-xs border-l-2 pl-3 py-1"
              style={{ borderColor: "var(--bt-taxi-primary)" }}>
            <div className="text-zinc-200">
              <span className="font-mono">{row.action}</span>
              {" · "}
              <span style={{ color: "var(--bt-taxi-primary)" }}>{row.key}</span>
              {" → "}
              <span className="font-semibold">{String(row.enabled)}</span>
            </div>
            <div className="text-zinc-500 mt-0.5">
              {fmt(row.at)} · {row.actor}
            </div>
          </li>
        ))}
      </ul>
    </PortalCard>
  );
}

function RouteInventoryCard() {
  const [routes, setRoutes] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    adminApi.get("/admin/app-management/routes")
      .then((r) => setRoutes(r.data?.routes || []))
      .catch(() => setRoutes([]));
  }, []);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return routes;
    return routes.filter((r) => r.path.toLowerCase().includes(needle));
  }, [routes, q]);
  return (
    <PortalCard Icon={RouteIcon} title="Route Inventory" testid="portal-card-routes" accent="secondary">
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={`Filter ${routes.length} routes…`}
        data-testid="portal-routes-filter"
        className="w-full mb-3 rounded-md px-3 py-1.5 text-sm bg-black/40 border text-white placeholder-zinc-500 outline-none focus:border-yellow-400"
        style={{ borderColor: "rgba(255,210,0,0.25)" }}
      />
      <div className="text-xs text-zinc-400 mb-2" data-testid="portal-routes-count">
        Showing {filtered.length} of {routes.length}
      </div>
      <ul className="space-y-1 max-h-60 overflow-y-auto pr-1 font-mono text-xs">
        {filtered.slice(0, 200).map((r) => (
          <li key={r.path}
              data-testid={`portal-route-${r.path}`}
              className="flex items-center justify-between gap-2">
            <span className="truncate text-zinc-200">{r.path}</span>
            <span className="text-zinc-500 whitespace-nowrap">{r.methods.join(",")}</span>
          </li>
        ))}
      </ul>
      {filtered.length > 200 && (
        <div className="mt-2 text-[11px] text-zinc-500">Showing first 200 rows · refine filter to see more.</div>
      )}
    </PortalCard>
  );
}

/* ──────────────────────────── Page shell ─────────────────────────── */
export default function AppManagementPortalPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);  // {authorized, role, can_write}

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/app-management/access-check");
        if (!cancelled) setAccess(data);
      } catch {
        if (!cancelled) setAccess({ authorized: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-300" data-testid="portal-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        <span>Checking App Management Portal access…</span>
      </div>
    );
  }

  if (!access?.authorized) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-950 text-zinc-200 p-6"
           data-testid="portal-denied">
        <Lock className="h-12 w-12 text-rose-400 mb-3" />
        <h1 className="text-xl font-bold mb-1">Access Denied</h1>
        <p className="text-sm text-zinc-400 mb-4 max-w-md text-center">
          The App Management Portal is reserved for super_user, general_partner,
          and dispatch roles. Your account does not carry any of those.
        </p>
        <Button
          variant="outline"
          onClick={() => navigate("/admin")}
          data-testid="portal-denied-back"
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />Back to Admin Console
        </Button>
      </div>
    );
  }

  const canWrite = !!access?.can_write;
  const roleLabel = (access?.role || "unknown").replace("_", " ");

  return (
    <div
      style={PORTAL_THEME_STYLE}
      data-app-management-portal="1"
      data-testid="app-management-portal-root"
      className="px-4 sm:px-6 lg:px-10 py-6"
    >
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin")}
          className="mb-3 -ml-2 text-zinc-400 hover:text-white"
          data-testid="portal-back"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />Back to Admin Console
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div
              className="rounded-lg p-2.5 flex-shrink-0"
              style={{
                backgroundColor: "rgba(255,210,0,0.10)",
                border: "1px solid var(--bt-taxi-primary)",
              }}
            >
              <ShieldCheck className="h-7 w-7" style={{ color: "var(--bt-taxi-primary)" }} />
            </div>
            <div>
              <h1
                className="text-2xl sm:text-3xl font-extrabold uppercase tracking-wide"
                style={{ color: "var(--bt-taxi-primary)" }}
                data-testid="portal-title"
              >
                App Management Portal
              </h1>
              <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
                Super-user control surface — inspect modules, toggle feature
                flags, audit RBAC counts, and review deployment state.
              </p>
              <div className="flex items-center gap-2 mt-2 text-xs">
                <span
                  className="uppercase tracking-wider font-bold px-2 py-1 rounded-full"
                  data-testid="portal-role-pill"
                  style={{
                    backgroundColor: "rgba(224,17,95,0.12)",
                    color: "var(--bt-taxi-secondary)",
                    border: "1px solid var(--bt-taxi-secondary)",
                  }}
                >{roleLabel}</span>
                <span
                  className="uppercase tracking-wider font-bold px-2 py-1 rounded-full"
                  data-testid="portal-write-pill"
                  style={{
                    backgroundColor: canWrite ? "rgba(255,210,0,0.12)" : "rgba(120,120,120,0.15)",
                    color: canWrite ? "var(--bt-taxi-primary)" : "#a1a1aa",
                    border: `1px solid ${canWrite ? "var(--bt-taxi-primary)" : "#52525b"}`,
                  }}
                >{canWrite ? "read + write" : "read-only"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Heatmap pause banner */}
        <div
          data-testid="portal-heatmap-banner"
          className="mt-5 flex items-start gap-3 rounded-lg border px-4 py-3"
          style={{
            borderColor: "var(--bt-taxi-danger)",
            backgroundColor: "rgba(211,47,47,0.08)",
          }}
        >
          <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: "var(--bt-taxi-danger)" }} />
          <div className="text-sm">
            <div className="font-semibold" style={{ color: "var(--bt-taxi-danger)" }}>
              Historical Ride Heatmap · Paused
            </div>
            <div className="text-zinc-300 mt-0.5">
              Heatmap generation logic remains disabled per operations directive.
              Feature flag <span className="font-mono">ride_heatmap_enabled</span> is locked off; contact the
              general partner to lift the freeze.
            </div>
          </div>
        </div>
      </div>

      {/* Portal grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="portal-grid">
        <ModuleRegistryCard />
        <FeatureFlagsCard canWrite={canWrite} />
        <RbacSnapshotCard />
        <ZoneSummaryCard />
        <DeploymentInfoCard />
        <AuditTailCard />
        <RouteInventoryCard />
      </div>
    </div>
  );
}
